import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { CachedOutlined } from '@mui/icons-material';

interface GlobalPullToRefreshIndicatorProps {
  isPulling: boolean;
  pullDistance: number;
  isRefreshing: boolean;
  threshold?: number;
}

/**
 * Global pull-to-refresh indicator for hard reloading the app
 * Shows at the top of the screen when user pulls down
 * Designed to help users recover from blank page issues after deployments
 */
export const GlobalPullToRefreshIndicator: React.FC<GlobalPullToRefreshIndicatorProps> = ({
  isPulling,
  pullDistance,
  isRefreshing,
  threshold,
}) => {
  if (!isPulling && !isRefreshing) return null;

  // Default threshold to 1/3 of screen height if not provided
  const effectiveThreshold = threshold ?? Math.floor(window.innerHeight / 3);
  const progress = Math.min(pullDistance / effectiveThreshold, 1);
  const scale = 0.5 + (progress * 0.5); // Scale from 0.5 to 1
  const opacity = progress;
  const rotation = progress * 360; // Rotate as user pulls

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pointerEvents: 'none',
        zIndex: 9999,
        transform: `translateY(${isPulling ? pullDistance * 0.5 : 0}px)`,
        transition: isPulling ? 'none' : 'transform 0.3s ease-out',
      }}
    >
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          backgroundColor: 'white',
          borderRadius: '50%',
          boxShadow: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `scale(${scale})`,
          opacity,
        }}
      >
        {isRefreshing ? (
          // Spinning loader when refreshing
          <CircularProgress size={32} sx={{ color: '#F5A800' }} />
        ) : (
          // Icon that rotates as user pulls
          <CachedOutlined
            sx={{
              fontSize: 32,
              color: '#F5A800',
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.1s ease-out',
            }}
          />
        )}
      </Box>

      {/* Text hint */}
      {isPulling && pullDistance >= effectiveThreshold && !isRefreshing && (
        <Box
          sx={{
            position: 'absolute',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              px: 2,
              py: 0.5,
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            Release to refresh
          </Typography>
        </Box>
      )}
    </Box>
  );
};
